import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { FINANCIAL_ROUTES } from "@/utils/financialFlow";
import { useSingleInsuranceRecord, INITIAL_WC } from "@/hooks/useInsuranceRecord";
import {
  ERROR_INPUT_CLASS,
  FieldError,
  isValidPhone,
  phoneDigits,
  formatPhoneDisplay,
  cn,
} from "@/components/insurance/insuranceForm";
import type { InsuranceInfo } from "@/store/insurance";

/** Field keys map directly to backend InsuranceInfo columns (proven in the Vue original). */
type WcForm = { empName: string; carrier: string; carrierPhone: string; idNum: string };

const EMPTY: WcForm = { empName: "", carrier: "", carrierPhone: "", idNum: "" };

export function WorkersCompensationPage() {
  const navigate = useNavigate();
  const { loading, record, saveField, flush } = useSingleInsuranceRecord({
    initial: INITIAL_WC,
    select: (list: InsuranceInfo[]) => list.find((r) => Number(r.wc) === 1),
  });

  const [form, setForm] = useState<WcForm>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof WcForm, string>>>({});

  // Seed once the existing record loads.
  useEffect(() => {
    if (record) {
      setForm({
        empName: record.empName ?? "",
        carrier: record.carrier ?? "",
        carrierPhone: (record.carrierPhone ?? "").replace(/\D/g, ""),
        idNum: record.idNum ?? "",
      });
    }
  }, [record]);

  const update = (field: keyof WcForm, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
    saveField(field, value);
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof WcForm, string>> = {};
    if (!form.empName.trim()) next.empName = "Employer name is required.";
    if (!form.carrier.trim()) next.carrier = "Adjuster name is required.";
    if (!form.carrierPhone.trim()) next.carrierPhone = "Adjuster phone is required.";
    else if (!isValidPhone(form.carrierPhone)) next.carrierPhone = "Enter a valid 10-digit phone.";
    if (!form.idNum.trim()) next.idNum = "Claim number is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goBack = async () => {
    await flush();
    navigate(FINANCIAL_ROUTES.q1);
  };
  const goToQ2 = async () => {
    await flush();
    navigate(FINANCIAL_ROUTES.q2);
  };
  const onContinue = async () => {
    if (validate()) await goToQ2();
  };

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content financial-responsibility-content flex justify-center px-4 py-6">
        <div className="complaint-history-card financial-step-card">
          <h2 className="mb-1 text-[20px] font-extrabold text-healthsource-dark">Workers' Compensation</h2>
          <p className="mb-6 text-[13px] text-healthsource-gray">
            Please enter your workers' compensation details. You can also skip this for now and
            provide it later.
          </p>

          {loading ? (
            <p className="py-8 text-center text-[13px] text-healthsource-gray">Loading…</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="wc-empName" required>Employer Name</Label>
                <Input
                  id="wc-empName"
                  placeholder="Employer Name"
                  value={form.empName}
                  onChange={(e) => update("empName", e.target.value)}
                  className={cn(errors.empName && ERROR_INPUT_CLASS)}
                  aria-invalid={errors.empName ? true : undefined}
                />
                <FieldError message={errors.empName} />
              </div>

              <div>
                <Label htmlFor="wc-carrier" required>Adjuster Name</Label>
                <Input
                  id="wc-carrier"
                  placeholder="Adjuster Name"
                  value={form.carrier}
                  onChange={(e) => update("carrier", e.target.value)}
                  className={cn(errors.carrier && ERROR_INPUT_CLASS)}
                  aria-invalid={errors.carrier ? true : undefined}
                />
                <FieldError message={errors.carrier} />
              </div>

              <div>
                <Label htmlFor="wc-carrierPhone" required>Adjuster Phone</Label>
                <Input
                  id="wc-carrierPhone"
                  type="tel"
                  inputMode="tel"
                  placeholder="(XXX) XXX-XXXX"
                  value={formatPhoneDisplay(form.carrierPhone)}
                  onChange={(e) => update("carrierPhone", phoneDigits(e.target.value))}
                  className={cn(errors.carrierPhone && ERROR_INPUT_CLASS)}
                  aria-invalid={errors.carrierPhone ? true : undefined}
                />
                <FieldError message={errors.carrierPhone} />
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="wc-idNum" required>Claim Number</Label>
                <Input
                  id="wc-idNum"
                  placeholder="Claim Number"
                  value={form.idNum}
                  onChange={(e) => update("idNum", e.target.value)}
                  className={cn(errors.idNum && ERROR_INPUT_CLASS)}
                  aria-invalid={errors.idNum ? true : undefined}
                />
                <FieldError message={errors.idNum} />
              </div>
            </div>
          )}
        </div>
      </div>

      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack}>Back</Button>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={goToQ2}>Skip for now</Button>
          <Button type="button" variant="primary" onClick={onContinue}>Continue</Button>
        </div>
      </WizardActionsBar>
    </div>
  );
}
